import React, { Dispatch, SetStateAction, useRef, useState } from "react";
import { useStore } from "@nanostores/react";

import { useSpringCarousel } from "react-spring-carousel";

import { $isCarouselDisplayed } from "../../utils/store";
import { Image, Product } from "../../utils/types";

import {
    ArrowLeft,
    ArrowRight,
    ArrowSmall,
} from "./alsoProducts";
import PaginationDots from "../shared/paginationDots";

import CarouselThumb from "./carouselThumb";
import CarouselItem from "./carouselItem";

import { __t } from "../../utils/translate";
import { getGatsbyImageData } from "../../utils/utils";

import { useProductContext } from "@/providers/productProvider";

type CarouselProps = {
    isFullScreen?: boolean;
    onClose?: () => void;
    className?: string;
    product: Product;
    sliderOpen?: boolean;
    setSliderOpen?: () => void;
};

const Carousel: React.FC<CarouselProps> = ({ className = "", product, isFullScreen, onClose, sliderOpen, setSliderOpen }) => {
    const isCarouselDisplayed = useStore($isCarouselDisplayed);
    const countImages = product.images?.length ? product.images.length + 1 : 1;

    return (
        isCarouselDisplayed
        && <RealCarousel isFullScreen={isFullScreen} onClose={onClose} className={className} product={product} sliderOpen={sliderOpen} setSliderOpen={setSliderOpen} />
        || <FakeCarousel className={className} product={product} />
    );
};

const RealCarousel: React.FC<CarouselProps> = ({ className = "", product, isFullScreen, onClose, sliderOpen, setSliderOpen }) => {
    const isCarouselDisplayed = useStore($isCarouselDisplayed);
    const { image, activePicture, setActivePicture } = useProductContext();

    const fullSliderRef = useRef(null);
    function handleClickOutside(event) {
        if (event.target === fullSliderRef.current) {
            setSliderOpen;
        }
    }

    const renderItems = () => {
        let items = [];
        const imageData = getGatsbyImageData(image.localFile)!;

        items.push({
            id: String(0),
            renderItem: (
                !product.acf.video_in_first_slide_url &&
                <CarouselItem
                    alt={image.alt}
                    idx={0}
                    imageData={imageData}
                    isFullScreen={isFullScreen}
                    className="MainImageShow"
                    loading="eager"
                />
                ||
                <ProductVideo
                    src={product.acf.video_in_first_slide_url}
                />
            ),
            renderThumb: (
                <CarouselThumb
                    alt={image.alt}
                    activePicture={activePicture}
                    idx={0}
                    imageData={imageData}
                    onClick={() => handleClick(0)}
                    isLast={1 === product.images.length}
                />
            )
        });

        product.images.map((image: Image, idx: number) => {
            const imageData = getGatsbyImageData(image.localFile)!;

            items.push({
                id: String(idx + 1),
                renderItem: (
                    <CarouselItem
                        alt={image.alt}
                        idx={idx + 1}
                        imageData={imageData}
                        isFullScreen={isFullScreen}
                    />
                ),
                renderThumb: (
                    <CarouselThumb
                        alt={image.alt}
                        activePicture={activePicture}
                        idx={idx + 1}
                        imageData={imageData}
                        onClick={() => handleClick(idx + 1)}
                        isLast={idx + 1 === product.images.length}
                    />
                )
            });
        });
        return items;
    };
    const { carouselFragment,
        thumbsFragment,
        slideToPrevItem,
        slideToNextItem,
        slideToItem,
        useListenToCustomEvent } =
        useSpringCarousel({
            withThumbs: true,
            withLoop: false,
            enableThumbsWrapperScroll: true,
            initialActiveItem: activePicture,
            thumbsSlideAxis: 'x',
            items: renderItems(),
        });

    const handleSlideLeft = () => {
        isCarouselDisplayed ? slideToPrevItem() : $isCarouselDisplayed.set(true);
        if (activePicture > 0) {
            isFullScreen ? setActivePicture(activePicture - 1) : slideToItem(activePicture - 1);
        } else {
            isFullScreen ? setActivePicture(product.images.length) : slideToItem(product.images.length);
        }
    };

    const handleSlideRight = () => {
        isCarouselDisplayed ? slideToNextItem() : $isCarouselDisplayed.set(true);
        if (activePicture !== product.images.length) {
            isFullScreen ? setActivePicture(activePicture + 1) : slideToItem(activePicture + 1);
        } else {
            isFullScreen ? setActivePicture(0) : slideToItem(0);
        }
    };

    useListenToCustomEvent((event) => {
        if (event?.currentItem && event?.currentItem?.index !== activePicture) {
            setActivePicture(event.currentItem.index);
        }
    });

    const handleClick = (idx: string | number) => {
        const index = typeof idx === 'string' ? parseInt(idx, 10) : idx;

        isCarouselDisplayed ? slideToItem(index) : $isCarouselDisplayed.set(true);
    };

    return (
        isFullScreen && (
            <div className={`fixed inset-0 flex items-center justify-center bg-black bg-opacity-70
                ${sliderOpen ? 'opacity-100 z-[90]' : 'opacity-0 -z-[90]'} transition-all duration-300`}>
                <div ref={fullSliderRef} onClick={handleClickOutside}
                    className="relative w-full h-full flex items-center justify-center"
                >
                    <div className="absolute top-3 left-3 text-small-14 text-white">
                        {activePicture + 1} / {product.images.length + 1}
                    </div>

                    <button aria-label="fullscreen slider close" className="absolute top-0 right-0 p-[10px] cursor-pointer bg-black/40" onClick={setSliderOpen}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 32 32" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M24.9441 8.94289C25.4648 8.42219 25.4648 7.57797 24.9441 7.05727C24.4234 6.53657 23.5792 6.53657 23.0585 7.05727L16.0013 14.1145L8.94411 7.05727C8.42341 6.53657 7.57919 6.53657 7.05849 7.05727C6.53779 7.57797 6.53779 8.42219 7.05849 8.94289L14.1157 16.0001L7.05849 23.0573C6.53779 23.578 6.53779 24.4222 7.05849 24.9429C7.57919 25.4636 8.42341 25.4636 8.94411 24.9429L16.0013 17.8857L23.0585 24.9429C23.5792 25.4636 24.4234 25.4636 24.9441 24.9429C25.4648 24.4222 25.4648 23.578 24.9441 23.0573L17.8869 16.0001L24.9441 8.94289Z" fill="#ccc" />
                        </svg>
                    </button>


                    <div className="absolute w-fit h-fit left-[6px] top-[46%] z-[100]">
                        <ArrowLeft
                            isDisabled={!activePicture}
                            className="w-[38px] h-[38px] p-[6px] bg-black/40 fill-stroke-color/70"
                            onClick={handleSlideLeft}
                            isFullscreen={true}
                        />
                    </div>
                    <div className="absolute w-fit h-fit right-[6px] top-[46%] z-[100]">
                        <ArrowRight
                            isDisabled={product.images.length == activePicture}
                            className="w-[38px] h-[38px] p-[6px] bg-black/40 fill-stroke-color/70"
                            onClick={handleSlideRight}
                            isFullscreen={true}
                        />
                    </div>
                    <div className={`${className} max-w-[67vh] lg:max-w-[77vh] mx-auto relative flex`}>
                        <div
                            className={`max-w-max mx-auto relative`}
                        >
                            <div className="overflow-hidden">
                                {carouselFragment}
                            </div>
                            <div className={`hidden `}>
                                {thumbsFragment}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        ) || (
            <div className={`${className} md:static flex-col relative flex`}>
                <div
                    className={`w-full lg:h-[383px] lg-hero:h-[450px] cursor-pointer md:mb-2 relative`}
                >
                    <div onClick={setSliderOpen} className="overflow-hidden">
                        {carouselFragment}
                    </div>
                    <div className="absolute w-fit h-fit -left-[5px] top-[46%] z-20">
                        <ArrowLeft
                            isDisabled={!activePicture}
                            className="w-10 h-10 rotate-90"
                            onClick={handleSlideLeft}
                        />
                    </div>
                    <div className="absolute w-fit h-fit -right-[5px] top-[46%] z-20">
                        <ArrowRight
                            isDisabled={product.images.length == activePicture}
                            className="w-10 h-10 rotate-[-90deg]"
                            onClick={handleSlideRight}
                        />
                    </div>
                </div>
                <div className="mb-1">
                    <div className={`md:flex hidden flex-row relative`}>
                        {thumbsFragment}
                        <div className="absolute w-fit h-fit -left-[8px] top-[40%] z-20">
                            <ArrowSmall
                                isDisabled={!activePicture}
                                className="rotate-180 mt-[3px]"
                                onClick={handleSlideLeft}
                            />
                        </div>
                        <div className="absolute w-fit h-fit -right-[8px] top-[40%] z-20">
                            <ArrowSmall
                                isDisabled={product.images.length == activePicture}
                                className="rotate-0"
                                onClick={handleSlideRight}
                            />
                        </div>
                    </div>
                    <div className={`bottom-[-12px] md:hidden absolute flex w-full justify-center z-20`}>
                        <PaginationDots
                            activeCardIdx={activePicture}
                            onDotClick={handleClick}
                            totalSlides={product.images.length + 1}
                        />
                    </div>
                </div>
            </div>
        )
    )
};

const FakeCarousel: React.FC<CarouselProps> = ({ className = "", product }) => {
    const isCarouselDisplayed = useStore($isCarouselDisplayed);
    const { image, activePicture, setActivePicture } = useProductContext();

    const handleClick = () => {
        $isCarouselDisplayed.set(true);
    };

    const image_data = getGatsbyImageData(image.localFile)?.images.fallback

    return (
        <div className={`${className} relative md:static flex flex-col-reverse md:flex-row-reverse lg:flex-row gap-3 lg:gap-4`}>
            <div className="">
                <div className="xl:h-[528px] lg:h-[388px] h-[420px] md:flex hidden flex-column relative">

                    {!!activePicture && (
                        <div className="absolute w-fit h-fit hidden md:flex
              md:left-7 lg:left-4 md:top-[-20px] xl:left-11 z-30">
                            <ArrowLeft
                                className="w-9 h-9 rotate-180"
                                onClick={handleClick}
                            />
                        </div>
                    )}

                    {product.images.length !== activePicture && (
                        <div className="absolute w-fit h-fit hidden md:flex
              md:left-7 lg:left-4 md:bottom-[-20px] xl:left-11 z-30">
                            <ArrowRight
                                className="w-9 h-9 rotate-0"
                                onClick={handleClick}
                            />
                        </div>
                    )}

                </div>
                <div className="absolute flex w-full justify-center bottom-6 md:hidden z-20">
                    <PaginationDots
                        activeCardIdx={activePicture}
                        onDotClick={handleClick}
                        totalSlides={product.images.length + 1}
                    />
                </div>
            </div>
            <div className="overflow-hidden md:mb-4 w-full lg:h-[467px] lg:w-[467px] xl:w-[550px] xl:h-[550px]">
                <div className="relative">
                    <img alt="" src="data:image/svg+xml;charset=utf-8,%3Csvg%20height='1024'%20width='1024'%20xmlns='http://www.w3.org/2000/svg'%20version='1.1'%3E%3C/svg%3E" />
                    <img
                        alt={image.alt}
                        src={image_data.src}
                        srcSet={image_data.srcSet}
                        sizes={image_data.sizes}
                        loading="eager"
                        decoding="async"
                        className="absolute w-full object-cover h-full top-0"
                    />
                </div>
            </div>

        </div>
    );
};

type ProductVideoProps = {
    src: string;
};

const ProductVideo: React.FC<ProductVideoProps> = ({ src }) => {
    return (
        <video
            src={src}
            autoPlay
            muted
            playsInline
            preload="auto"
            className="w-full"
            loop={true}
        />
    );
};

export default Carousel;